import { ReactNode } from 'react';

import round from 'lodash/round';

import {
  DataPopper,
  DataPopperRow,
  DataPopperRowProps,
  TooltipProps,
  Typography,
  Box,
  tokens,
} from '@cast/design-system';

import { DataTooltip } from '@cast/components-data-tooltip';
import { ComputeSpendType, computeSpendColorsMap } from 'types/cost-report';

type TooltipRowProps = {
  type: `${ComputeSpendType}`;
  title: string;
  value: number | string | ReactNode;
  percentage: string;
};

const TooltipRow = ({ type, title, value, percentage }: TooltipRowProps) => {
  return (
    <DataPopperRow
      indicator={{ color: computeSpendColorsMap[type]! }}
      left={
        <Typography variant="L10R" color="grey.900">
          {title}
        </Typography>
      }
      right={
        <>
          <Typography variant="P10R" color="grey.900">
            {value}
          </Typography>
          <Typography
            variant="P10R"
            color={tokens.theme.fg.structural.secondary}
            textAlign="right"
            minWidth="40px"
          >
            {percentage}
          </Typography>
        </>
      }
    />
  );
};

type Props = {
  id?: string;
  children: ReactNode;
  renderValue?: (value: number) => ReactNode;
  titleRowProps?: DataPopperRowProps;
  fullWidth?: boolean;
  tooltipProps?: Partial<TooltipProps>;
  showStorage?: boolean;
} & {
  onDemand: number;
  spot: number;
  fallback: number;
  storage?: number;
};

export const NodesAnalysis = ({
  id,
  onDemand = 0,
  spot = 0,
  fallback = 0,
  storage = 0,
  renderValue,
  titleRowProps,
  fullWidth,
  tooltipProps,
  showStorage = false,
  children,
}: Props) => {
  let total = (onDemand || 0) + (spot || 0) + (fallback || 0);

  if (showStorage) {
    total += storage || 0;
  }

  const onDemandPercentage = (onDemand * 100) / (total || 1);
  const spotPercentage = (spot * 100) / (total || 1);
  const fallbackPercentage = (fallback * 100) / (total || 1);
  const storagePercentage = (storage * 100) / (total || 1);

  return (
    <DataTooltip
      id={id}
      title={
        <DataPopper size="small" sx={{ width: fullWidth ? '100%' : 210 }}>
          {titleRowProps && <DataPopperRow dividerBottom {...titleRowProps} />}
          {showStorage && (
            <TooltipRow
              type="storage"
              title="storage"
              value={renderValue ? renderValue(storage) : storage}
              percentage={`${round(storagePercentage, 2)}%`}
            />
          )}

          <TooltipRow
            type="onDemand"
            title="on-demand"
            value={renderValue ? renderValue(onDemand) : onDemand}
            percentage={`${round(onDemandPercentage, 2)}%`}
          />
          <TooltipRow
            type="fallback"
            title="fallback"
            value={renderValue ? renderValue(fallback) : fallback}
            percentage={`${round(fallbackPercentage, 2)}%`}
          />
          <TooltipRow
            type="spot"
            title="spot"
            value={renderValue ? renderValue(spot) : spot}
            percentage={`${round(spotPercentage, 2)}%`}
          />
        </DataPopper>
      }
      placement="top"
      disableHoverListener={!total}
      {...tooltipProps}
    >
      <Box
        data-testid="analysis-by-resource-offering"
        flexGrow={1}
        maxWidth={fullWidth ? '100%' : 120}
        minHeight={8} // Improves hover effect
      >
        {children}
      </Box>
    </DataTooltip>
  );
};
